import { Injectable } from '@angular/core';
import { MmAlertSize, MmAlertType } from '@metromarkets/components';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, skip, switchMap, tap } from 'rxjs/operators';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { environment } from '~env/environment';
import { AddressBookHttpService } from '~shared/modules/addresses/services/address-book-http.service';
import { BadRequestHttpError, GeneralHttpError } from '~shared/modules/error-handler';
import { NotificationService } from '~shared/services/notification.service';
import { Address } from '../../models';
import {
  AddressesActionTypes,
  ClearAddresses,
  CreateAddress,
  CreateAddressBatch,
  CreateAddressBatchFailure,
  CreateAddressBatchSuccess,
  CreateAddressFailure,
  CreateAddressSuccess,
  ReadAddresses,
  ReadAddressesFailure,
  ReadAddressesSuccess,
  SelectBillingAddress,
  SelectShippingAddress,
  UpdateAddress,
  UpdateAddressFailure,
  UpdateAddressSuccess,
  ValidateShippingAddress,
  ValidateShippingAddressFailure,
  ValidateShippingAddressSuccess
} from './addresses.actions';
import { CheckoutService } from '../../services';

const ADDRESS_ERROR_CSS_API_CONFLICT = 'Conflict';
const ACCOUNT_ROLE_ENTREPRENEUR = 'ROLE_ENTREPRENEUR';

@Injectable()
export class AddressesEffects {
  @Effect()
  validateShippingAddress$ = this.actions$.pipe(
    ofType(AddressesActionTypes.ReadAddressesSuccess),
    map((action: ReadAddressesSuccess) => new ValidateShippingAddress(this.getSelectedAddresses(action.payload)))
  );

  @Effect()
  requestShippingAddressValidation$ = this.actions$.pipe(
    ofType(AddressesActionTypes.ValidateShippingAddress),
    switchMap((action: ValidateShippingAddress) =>
      this.checkoutService.validateShippingAvailability(action.payload)
    ),
    map(isValid => isValid
      ? new ValidateShippingAddressSuccess()
      : new ValidateShippingAddressFailure()
    ),
    catchError(() => of(new ValidateShippingAddressFailure()))
  );

  private getSelectedAddresses(addresses: Address[]): [Address, Address] {
    return [
      addresses.find(address => address.defaultShipping),
      addresses.find(address => address.defaultBilling)
    ];
  }

  @Effect()
  readAddresses$ = this.actions$.pipe(
    ofType(AddressesActionTypes.ReadAddresses),
    mergeMap(() =>
      this.addressBookHttpService.read().pipe(
        map((addresses: Address[]) => new ReadAddressesSuccess(addresses)),
        catchError((error) => of(new ReadAddressesFailure(error)))
      )
    )
  );

  @Effect()
  createAddressBatch$ = this.actions$.pipe(
    ofType(AddressesActionTypes.CreateAddressBatch),
    mergeMap((action: CreateAddressBatch) =>
      this.addressBookHttpService.batchCreate(action.payload.addresses).pipe(
        map((addresses: Address[]) => {
          return new CreateAddressBatchSuccess({ addresses });
        }),
        catchError((error) => {
          if (error instanceof BadRequestHttpError || error instanceof GeneralHttpError) {
            this.handleHttpError(error);
          }

          //Extra check added for calls from CSS Api which don't match our BE Contract
          if (error?.statusText) {
            this.handleHttpErrorCSSApi(error);
          }

          return of(new CreateAddressBatchFailure({ error }));
        })
      )
    )
  );

  @Effect()
  createAddress$ = this.actions$.pipe(
    ofType(AddressesActionTypes.CreateAddress),
    mergeMap((action: CreateAddress) =>
      this.addressBookHttpService.create(action.payload).pipe(
        map((address: Address) => {
          return new CreateAddressSuccess(address);
        }),
        catchError((error) => {
          if (error instanceof BadRequestHttpError || error instanceof GeneralHttpError) {
            this.handleHttpError(error);
          }

          //Extra check added for calls from CSS Api which don't match our BE Contract
          if (error?.statusText) {
            this.handleHttpErrorCSSApi(error);
          }

          return of(new CreateAddressFailure(error));
        })
      )
    )
  );

  @Effect()
  updateAddress$ = this.actions$.pipe(
    ofType(AddressesActionTypes.UpdateAddress),
    mergeMap((action: UpdateAddress) =>
      this.addressBookHttpService
        .update({
          ...action.payload.address,
          id: action.payload.id,
        })
        .pipe(
          map((address: Address) => {
            return new UpdateAddressSuccess(address);
          }),
          catchError((error) => {
            return this.handleAddressUpdateFailure(error);
          })
        )
    )
  );

  @Effect()
  setDefaultShipping$ = this.actions$.pipe(
    ofType(AddressesActionTypes.SelectShippingAddress),
    mergeMap((action: SelectShippingAddress) =>
      this.addressBookHttpService
        .update({
          ...action.payload,
          defaultShipping: true,
        })
        .pipe(
          map((address: Address) => {
            return new UpdateAddressSuccess(address);
          }),
          catchError((error) => {
            return this.handleAddressUpdateFailure(error);
          })
        )
    )
  );

  @Effect()
  setDefaultBilling$ = this.actions$.pipe(
    ofType(AddressesActionTypes.SelectBillingAddress),
    mergeMap((action: SelectBillingAddress) =>
      this.addressBookHttpService
        .update({
          ...action.payload,
          defaultBilling: true,
        })
        .pipe(
          map((address: Address) => {
            return new UpdateAddressSuccess(address);
          }),
          catchError((error) => {
            return this.handleAddressUpdateFailure(error);
          })
        )
    )
  );

  @Effect()
  refreshAddresses$ = this.actions$.pipe(
    ofType(
      AddressesActionTypes.CreateAddressSuccess,
      AddressesActionTypes.UpdateAddressSuccess,
      AddressesActionTypes.CreateAddressBatchSuccess
    ),
    map(() => new ReadAddresses())
  );

  @Effect()
  clearAddresses$ = this.identityService.isAuthorized$.pipe(
    skip(1),
    filter((isAuthorized: boolean) => !isAuthorized),
    map(() => new ClearAddresses())
  );

  constructor(
    private actions$: Actions,
    private addressBookHttpService: AddressBookHttpService,
    private notificationService: NotificationService,
    private identityService: IdentityService,
    private accountInfoService: AccountInfoService,
    private checkoutService: CheckoutService
  ) { }

  private handleAddressUpdateFailure(
    error: BadRequestHttpError | GeneralHttpError | any
  ): Observable<UpdateAddressFailure> {
    if (error instanceof BadRequestHttpError || error instanceof GeneralHttpError) {
      this.handleHttpError(error);
    } else if (error.invalidParams?.province) {
      this.handleProvinceError(error);
    }

    return of(new UpdateAddressFailure(error));
  }

  private handleProvinceError(error): void {
    this.notificationService.notify({
      message: error.invalidParams.province.reason,
      type: MmAlertType.error,
      size: MmAlertSize.large,
    });
  }

  private handleHttpError(error: BadRequestHttpError | GeneralHttpError): void {
    const { title } = error;

    let errorMessage = title;
    if (error instanceof BadRequestHttpError) {
      const invalidParamMsg = Object.values(error.invalidParams)[0].reason;

      if (invalidParamMsg) {
        errorMessage = invalidParamMsg;
      }
    }

    this.notificationService.notify({
      message: errorMessage,
      type: MmAlertType.error,
      size: MmAlertSize.large,
    });
  }

  private handleHttpErrorCSSApi(error): void {
    let myMetroAddressBookUrl: string;

    if (error?.error?.message === ADDRESS_ERROR_CSS_API_CONFLICT) {
      let context: string;
      const business = ACCOUNT_ROLE_ENTREPRENEUR;

      this.accountInfoService.getAccountInfo().subscribe((account) => {
        context = account?.activeContext;
        if (account?.role === business) {
          myMetroAddressBookUrl = `${environment.myMetroBaseUrl}/business/account/${context}/addresses`;
        } else {
          myMetroAddressBookUrl = `${environment.myMetroBaseUrl}/${context}/addresses`;
        }
      });

      this.notificationService.notify(
        {
          message: 'CHECKOUT.ERROR.ADDRESS_ALREADY_EXISTS',
          type: MmAlertType.error,
          size: MmAlertSize.large,
        },
        { myMetroAddressBookUrl }
      );
    }
  }
}
